/**
 * THE WORKSPACE WORKING CALENDAR — which days the organisation actually works, so a
 * forecast, a nudge or a schedule never assumes progress on a day nobody is in
 * (project-milestone-forecast-from-cadence, v3). ONE calendar per workspace: the
 * working WEEK (which weekdays count) and the HOLIDAYS on top of it (a dated close,
 * one day or a range, optionally repeating every year).
 *
 * PURE, AND LOCAL-MIDNIGHT. Every helper walks a calendar day at a time through
 * `Date`, comparing at the reader's own local midnight — the same rule src/sprints.ts
 * and idle-nudge's businessDaysBetween already use, so "what is a weekend" is the
 * reader's week, not UTC's, and a day that crosses a daylight-saving change is still
 * one day. It takes the calendar as an argument and reads no store, so "given this
 * calendar, which days are working" is proven on plain fixtures.
 *
 * WHY IT IS NOT IN sprints.ts. sprints.ts counts CALENDAR days and is imported almost
 * everywhere; the working-day math is a separate concern with its own model, so it
 * lives on its own and the surfaces that need it (the cadence forecast, the idle
 * nudge, the stale-to-do signal, progress-vs-schedule, dependency cascades, the
 * burndown ideal line) import it where they need it.
 */

/** Which weekdays are working, `0` = Sunday … `6` = Saturday. The default is the
 *  Monday–Friday week; a workspace can set its own (e.g. Sunday–Thursday). */
export type WorkingWeek = number[];
export const DEFAULT_WORKING_WEEK: WorkingWeek = [1, 2, 3, 4, 5];

/**
 * One non-working stretch on top of the week. `date` is the first day (`YYYY-MM-DD`);
 * `until` is an inclusive last day for a multi-day close (a week-long shutdown),
 * omitted for a single day. `annual` repeats it every year — matched on month and
 * day, the year ignored — for a fixed public holiday like New Year's Day.
 */
export interface Holiday {
  name: string;
  date: string;
  until?: string;
  annual?: boolean;
}

/** A workspace's working calendar: the week, and the holidays over it. */
export interface WorkCalendar {
  week: WorkingWeek;
  holidays: Holiday[];
}

/** The calendar a workspace with nothing configured runs on — a Mon–Fri week and
 *  no holidays, so every surface behaves exactly as it did before a calendar was
 *  set (weekends already excluded where a working-day count was already used). */
export const DEFAULT_CALENDAR: WorkCalendar = { week: DEFAULT_WORKING_WEEK, holidays: [] };

/** `YYYY-MM-DD` → `[y, m, d]` (m is 1–12), tolerant of a missing part. */
function ymd(date: string): [number, number, number] {
  const [y, m, d] = date.split('-').map(Number);
  return [y || 1970, m || 1, d || 1];
}

/** A `YYYY-MM-DD` as its local-midnight `Date`. */
function at(date: string): Date {
  const [y, m, d] = ymd(date);
  return new Date(y, m - 1, d);
}

/** A local-midnight `Date` back to `YYYY-MM-DD`. */
function iso(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** mmdd as a comparable number (e.g. 25 Dec → 1225), for annual matching. */
const mmdd = (m: number, d: number) => m * 100 + d;

/** Whether `date` (`YYYY-MM-DD`) falls inside a holiday — a one-off range by plain
 *  date order, an annual one by month/day with the year ignored. An annual range is
 *  assumed not to cross the year end (a close that spans New Year is kept as two
 *  one-off entries, which is also how a shutdown with different dates each year is
 *  entered). Returns the holiday's name, or undefined. */
export function holidayOn(date: string, cal: WorkCalendar): string | undefined {
  return holidayEntryOn(date, cal)?.name;
}

/** As {@link holidayOn}, but returns the whole holiday entry so a caller can name
 *  AND date the close — the forecast foot-cue uses it to say which close moved the
 *  prediction and over what span. */
export function holidayEntryOn(date: string, cal: WorkCalendar): Holiday | undefined {
  const [, m, d] = ymd(date);
  const key = mmdd(m, d);
  for (const h of cal.holidays) {
    const [, sm, sd] = ymd(h.date);
    const [, em, ed] = ymd(h.until ?? h.date);
    if (h.annual) {
      if (key >= mmdd(sm, sd) && key <= mmdd(em, ed)) return h;
    } else if (date >= h.date && date <= (h.until ?? h.date)) {
      return h;
    }
  }
  return undefined;
}

/** The working week as a fast lookup, falling back to Mon–Fri when a calendar
 *  somehow carries an empty week (a workspace cannot work zero days). */
function weekSet(cal: WorkCalendar): Set<number> {
  const w = cal.week && cal.week.length ? cal.week : DEFAULT_WORKING_WEEK;
  return new Set(w);
}

/** Whether a day is one the workspace works: a weekday in the working week, and not
 *  a holiday. */
export function isWorkingDay(date: string, cal: WorkCalendar): boolean {
  const dow = at(date).getDay();
  return weekSet(cal).has(dow) && holidayOn(date, cal) === undefined;
}

/** The first working day on or after `date`. */
export function nextWorkingDay(date: string, cal: WorkCalendar): string {
  const cur = at(date);
  const week = weekSet(cal);
  // Bounded so a calendar that somehow made every day a holiday cannot loop forever.
  for (let i = 0; i < 3660; i++) {
    const day = iso(cur);
    if (week.has(cur.getDay()) && holidayOn(day, cal) === undefined) return day;
    cur.setDate(cur.getDate() + 1);
  }
  return iso(cur);
}

/** A `Date` floored to local midnight. */
const midnight = (ms: number): Date => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/**
 * WORKING DAYS BETWEEN TWO INSTANTS — the calendar-aware businessDaysBetween. Counts
 * the working days STRICTLY AFTER `aMs`'s day up to and INCLUDING `bMs`'s day, so a
 * Friday → Monday with a Mon–Fri week is one working day, and a Monday that is a
 * holiday makes it zero. A span that does not move forward is 0. Walked a day at a
 * time at local midnight, for the daylight-saving reason addDays gives.
 */
export function workingDaysBetween(aMs: number, bMs: number, cal: WorkCalendar): number {
  const end = midnight(bMs).getTime();
  const cur = midnight(aMs);
  cur.setDate(cur.getDate() + 1); // the morning after
  const week = weekSet(cal);
  let count = 0;
  while (cur.getTime() <= end) {
    if (week.has(cur.getDay()) && holidayOn(iso(cur), cal) === undefined) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/**
 * HOLIDAYS LOST IN A WINDOW — how many days in `(aMs, bMs]` are WORKING weekdays the
 * workspace nonetheless closes for a holiday. Weekends are NOT counted: a team's
 * weekly throughput already prices its ordinary weekends in, so only a holiday the
 * normal rhythm did not contain is "extra" time the work will not progress. This is
 * what the cadence forecast adds to a projected finish so a known future shutdown
 * pushes the date out (and nothing else moves). Strictly after `aMs`'s day through
 * `bMs`'s day, matching workingDaysBetween's window.
 */
export function holidayDaysBetween(aMs: number, bMs: number, cal: WorkCalendar): number {
  const end = midnight(bMs).getTime();
  const cur = midnight(aMs);
  cur.setDate(cur.getDate() + 1);
  const week = weekSet(cal);
  let count = 0;
  while (cur.getTime() <= end) {
    if (week.has(cur.getDay()) && holidayOn(iso(cur), cal) !== undefined) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/**
 * WORKING DAYS IN THE INCLUSIVE WINDOW `[fromISO .. toISO]` — the working-day analog
 * of sprints.ts `daysBetween` (which is end-inclusive and counts every calendar day).
 * Both endpoints are counted when they are working days, so a one-day window on a
 * working day is 1. A window entirely on non-working days (a close over a weekend) is
 * 0; callers that divide by it guard to ≥1, exactly as `daysBetween` guarantees. Used
 * by progress-vs-schedule and the burndown ideal line, which count working days always
 * (Vinnie, 28 Oct): on the default Mon–Fri calendar weekends already drop out.
 */
export function workingDaysInclusive(fromISO: string, toISO: string, cal: WorkCalendar): number {
  const a = at(fromISO).getTime();
  const b = at(toISO).getTime();
  if (b < a) return 0;
  const week = weekSet(cal);
  const cur = new Date(a);
  let count = 0;
  while (cur.getTime() <= b) {
    if (week.has(cur.getDay()) && holidayOn(iso(cur), cal) === undefined) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/**
 * THE DATE `n` WORKING DAYS AFTER `fromISO` — the working-day analog of `addDays` for a
 * forward projection (a card's projected finish counts working days). `fromISO` itself
 * is not counted; the result is the day on which the nth working day falls, so n=1 is
 * the next working day. n ≤ 0 returns `fromISO` unchanged. Bounded so a calendar that
 * somehow made every day non-working cannot loop forever.
 */
export function addWorkingDays(fromISO: string, n: number, cal: WorkCalendar): string {
  if (n <= 0) return fromISO;
  const week = weekSet(cal);
  const cur = at(fromISO);
  let left = Math.ceil(n);
  for (let i = 0; i < 36600 && left > 0; i++) {
    cur.setDate(cur.getDate() + 1);
    if (week.has(cur.getDay()) && holidayOn(iso(cur), cal) === undefined) left -= 1;
  }
  return iso(cur);
}

/**
 * THE WORKING-WEEKDAY HOLIDAY INSTANTS in `(fromMs, toMs]`, local midnight, sorted
 * ascending. The cadence forecast precomputes this ONCE and then counts how many
 * holidays fall before a given finish with a binary search, rather than walking the
 * calendar a day at a time for every one of its ten thousand trials. Weekends are
 * excluded for the same reason holidayDaysBetween excludes them — the weekly pace
 * already holds a team's ordinary weekends.
 */
export function workingHolidayInstants(fromMs: number, toMs: number, cal: WorkCalendar): number[] {
  const out: number[] = [];
  if (!calendarIsActive(cal) || toMs <= fromMs) return out;
  const end = midnight(toMs).getTime();
  const cur = midnight(fromMs);
  cur.setDate(cur.getDate() + 1);
  const week = weekSet(cal);
  while (cur.getTime() <= end) {
    if (week.has(cur.getDay()) && holidayOn(iso(cur), cal) !== undefined) out.push(cur.getTime());
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/**
 * THE WORKSPACE CLOSES stepped over in `(fromMs, toMs]` — the holiday entries that
 * fall on a WORKING weekday inside the window, each once, in the order their first
 * such day appears. The cadence forecast's foot-cue reads this to NAME the close(s)
 * that pushed the prediction out (0216, v3): one close → it is named and dated; more
 * than one → they are counted. Weekends are excluded for the same reason the forecast
 * excludes them — the weekly pace already holds a team's ordinary weekends.
 */
export function closesBetween(fromMs: number, toMs: number, cal: WorkCalendar): Holiday[] {
  if (!calendarIsActive(cal) || toMs <= fromMs) return [];
  const end = midnight(toMs).getTime();
  const cur = midnight(fromMs);
  cur.setDate(cur.getDate() + 1);
  const week = weekSet(cal);
  const seen = new Set<Holiday>();
  const out: Holiday[] = [];
  while (cur.getTime() <= end) {
    if (week.has(cur.getDay())) {
      const h = holidayEntryOn(iso(cur), cal);
      if (h && !seen.has(h)) { seen.add(h); out.push(h); }
    }
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/**
 * A FINISH INSTANT PUSHED PAST KNOWN FUTURE HOLIDAYS — a date-granular fixed point.
 * `holidayInstants` is the sorted list from {@link workingHolidayInstants} (over a
 * horizon comfortably past the finish); this adds one day to the finish for every
 * holiday on or before it, repeating until stable, so a close that the extension
 * itself pushes the date INTO is still counted. Returns the finish unchanged when the
 * calendar is inert or nothing falls before it. A pure function of its inputs, so the
 * forecast engine and the snapshot writer that both call it stay in lock-step.
 */
export function pushPastHolidays(fromMs: number, finishMs: number, holidayInstants: number[]): number {
  if (holidayInstants.length === 0 || finishMs <= fromMs) return finishMs;
  // Count of instants <= ms, by binary search (the list is sorted ascending).
  const countLE = (ms: number): number => {
    let lo = 0;
    let hi = holidayInstants.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (holidayInstants[mid] <= ms) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };
  let adj = finishMs;
  for (let i = 0; i < 64; i++) {
    const next = finishMs + countLE(adj) * 86_400_000;
    if (next === adj) break;
    adj = next;
  }
  return adj;
}

/** Whether a calendar actually constrains anything — a non-standard week or any
 *  holiday. A workspace on the default empty calendar needs no working-day pass, so
 *  callers can skip the extra work and read exactly as before. */
export function calendarIsActive(cal: WorkCalendar | null | undefined): cal is WorkCalendar {
  if (!cal) return false;
  if (cal.holidays && cal.holidays.length) return true;
  const w = cal.week ?? [];
  if (w.length !== DEFAULT_WORKING_WEEK.length) return true;
  const def = new Set(DEFAULT_WORKING_WEEK);
  return w.some((d) => !def.has(d));
}
